export const RECOVERY_VERSION_KEY = (workspaceId: string) =>
  `sl_recovery_version_${workspaceId}`;

export interface WorkspaceRecoveryVersionCheck {
  serverVersion: number;
  localVersionStr: string | null;
  /** Whether this browser held local databases of the workspace before this page opened it. */
  hadLocalWorkspaceDatabases: boolean;
}

/**
 * The local databases of this browser when the page started, before the page
 * opened a workspace and created databases of its own.
 */
const databasesAtPageStart: Promise<IDBDatabaseInfo[]> =
  typeof indexedDB !== 'undefined' && indexedDB.databases
    ? indexedDB.databases().catch(() => [])
    : Promise.resolve([]);

export async function hadLocalWorkspaceDatabasesAtPageStart(
  workspaceId: string
): Promise<boolean> {
  const databases = await databasesAtPageStart;
  return databases.some(database => database.name?.includes(workspaceId));
}

const RECOVERY_VERSION_KEY_PREFIX = RECOVERY_VERSION_KEY('');

function readRecordedVersions(): ReadonlyMap<string, number> {
  const versions = new Map<string, number>();
  try {
    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);
      if (!key?.startsWith(RECOVERY_VERSION_KEY_PREFIX)) continue;
      const version = Number.parseInt(localStorage.getItem(key) ?? '', 10);
      if (Number.isFinite(version)) versions.set(key, version);
    }
  } catch {
    // A browser without storage access has recorded nothing.
  }
  return versions;
}

const recordedVersionsAtPageStart = readRecordedVersions();

/**
 * The recovery version this browser had recorded for the workspace when the
 * page started: the local data this page opened was cleared for that version.
 */
export function recoveryVersionAtPageStart(workspaceId: string): number | null {
  return (
    recordedVersionsAtPageStart.get(RECOVERY_VERSION_KEY(workspaceId)) ?? null
  );
}

/**
 * A browser without a recorded recovery version holds stale data only when
 * it brought local databases of the workspace along. Databases the running
 * visit created itself are a first visit: there is nothing stale to clear.
 */
export function shouldRefreshLocalWorkspaceCache({
  serverVersion,
  localVersionStr,
  hadLocalWorkspaceDatabases,
}: WorkspaceRecoveryVersionCheck): boolean {
  if (!Number.isFinite(serverVersion) || serverVersion <= 0) {
    return false;
  }

  if (localVersionStr === null) {
    return hadLocalWorkspaceDatabases;
  }

  const localVersion = Number.parseInt(localVersionStr, 10);
  if (!Number.isFinite(localVersion)) {
    return hadLocalWorkspaceDatabases;
  }

  return serverVersion > localVersion;
}
